import React from "react";

/**
 * Remote Game Launch — see docs/remote-game-launch-plan.md
 *
 * When isRemote is true, the app is running inside an iframe launched from
 * an external page. Every lobby-return path branches on this: kiosk → lobby
 * as today, remote → fire postMessage('pt:gameComplete') to the parent and
 * render the safety screen.
 */
export const RemoteLaunchContext = React.createContext({
    isRemote: false,
    parentOrigin: null,
    sessionId: null,
    displayOptions: { keyboard: "on", printReceipt: false },
    /** true iff the registration modal ran in this session */
    registeredThisSession: false,
    setRegisteredThisSession: () => {},
});

const STORAGE_KEY = "pt3_remote_launch";

const KEYBOARD_MODES = ["on", "off", "small"];

export function normalizeKeyboardMode(value){
    return KEYBOARD_MODES.indexOf(value) >= 0 ? value : "on";
}

/**
 * Apply / clear a body class so the on-screen keyboard's CSS can react to
 * the launching page's chosen mode. Single seam — keeps VirtualKeyboard,
 * Home, Login, and game pages oblivious to remote-launch state.
 */
export function applyKeyboardMode(mode){
    if (typeof document === "undefined" || !document.body) return;
    var normalized = normalizeKeyboardMode(mode);
    var cls = document.body.classList;
    cls.remove("pt-kb-on", "pt-kb-off", "pt-kb-small");
    cls.add("pt-kb-" + normalized);
}

export function clearKeyboardMode(){
    if (typeof document === "undefined" || !document.body) return;
    document.body.classList.remove("pt-kb-on", "pt-kb-off", "pt-kb-small");
}

/**
 * True when this session came in through /go/<slug> — a standalone web page in
 * an ordinary browser — rather than an iframe embedded by a partner site.
 *
 * The distinction matters at three seams: there is no parent frame to
 * postMessage on completion, the player identifies themselves inside the game
 * instead of arriving pre-identified, and the page is allowed to scroll.
 */
export function isWebSession(session){
    var opts = (session && session.displayOptions) || {};
    return opts.mode === "web";
}

/* ---------------------------------------------------------------------------
 * Web-mode backdrop: the venue logo tiled faintly behind the centred column.
 *
 * Built as an inline SVG <pattern> rather than a CSS background, because CSS
 * cannot do what is needed here: `background-repeat` tiles at `background-size`,
 * so a raster background image always butts edge to edge. There is no way to
 * leave a gap between tiles, and no way to offset alternate rows.
 *
 * <pattern> separates the two: the tile box is 100x200, and the logo is placed
 * inside it twice at 50px wide. That gives both the spacing and the offset in
 * one browser-native, GPU-composited fill — no canvas (which cross-origin media
 * would taint), no data URI, no repeated DOM elements, and no JavaScript once
 * it is up.
 *
 *   tile 100 x 200        logo at (0,0) and (50,100)
 *   ┌──────────────┐
 *   │ ▓            │      row 1 starts at x=0, repeats every 100
 *   │              │
 *   │       ▓      │      row 2 offset by half a cell -> diamond lattice
 *   │              │
 *   └──────────────┘
 *
 * Density: each 50px logo owns a 100x100 area of the tile, so it covers a
 * quarter of it -- one pixel of logo to three of blank space, as specified.
 * ------------------------------------------------------------------------- */

const BACKDROP_ID = "pt-web-backdrop";
const PATTERN_ID = "pt-web-logo-pattern";

const LOGO_WIDTH = 50;    // rendered width of one mark
const CELL_WIDTH = 100;   // horizontal repeat: logos at x = 0, 100, 200 ...
const CELL_HEIGHT = 200;  // two rows per tile, so the offset row repeats cleanly

const SVG_NS = "http://www.w3.org/2000/svg";
const XLINK_NS = "http://www.w3.org/1999/xlink";

function removeBackdrop(){
    var existing = document.getElementById(BACKDROP_ID);
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
}

/**
 * Build (or rebuild) the tiled backdrop for `logoUrl`.
 *
 * Constructed with DOM APIs rather than innerHTML so the URL is never parsed as
 * markup — it reaches the document as an attribute value and nothing else.
 */
function renderBackdrop(logoUrl){
    removeBackdrop();
    if (!logoUrl) return;

    var host = document.createElement("div");
    host.id = BACKDROP_ID;
    host.setAttribute("aria-hidden", "true");
    host.style.cssText = [
        "position:fixed",
        "inset:0",
        "z-index:0",
        "pointer-events:none",
        "opacity:0.2",
    ].join(";");

    var svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("width", "100%");
    svg.setAttribute("height", "100%");
    svg.style.display = "block";

    var defs = document.createElementNS(SVG_NS, "defs");
    var pattern = document.createElementNS(SVG_NS, "pattern");
    pattern.setAttribute("id", PATTERN_ID);
    pattern.setAttribute("width", String(CELL_WIDTH));
    pattern.setAttribute("height", String(CELL_HEIGHT));
    pattern.setAttribute("patternUnits", "userSpaceOnUse");

    // Two marks per tile: one on the baseline row, one offset half a cell in
    // both axes. Repeating that tile produces the diamond lattice.
    [[0, 0], [CELL_WIDTH / 2, CELL_HEIGHT / 2]].forEach(function(pos){
        var image = document.createElementNS(SVG_NS, "image");
        image.setAttribute("x", String(pos[0]));
        image.setAttribute("y", String(pos[1]));
        // Width only — height is left to preserveAspectRatio, so a logo that is
        // not square keeps its proportions instead of being squashed.
        image.setAttribute("width", String(LOGO_WIDTH));
        image.setAttribute("href", logoUrl);
        // Safari still wants the xlink form.
        image.setAttributeNS(XLINK_NS, "xlink:href", logoUrl);
        pattern.appendChild(image);
    });

    defs.appendChild(pattern);
    svg.appendChild(defs);

    var rect = document.createElementNS(SVG_NS, "rect");
    rect.setAttribute("width", "100%");
    rect.setAttribute("height", "100%");
    rect.setAttribute("fill", "url(#" + PATTERN_ID + ")");
    svg.appendChild(rect);

    host.appendChild(svg);
    document.body.insertBefore(host, document.body.firstChild);
}

/**
 * Web-mode presentation: scrolling, the centred column, and the tiled logo.
 *
 * Three things hang off this one seam.
 *
 * 1. App.css sets `body { overflow: hidden }`, right for a kiosk — a fixed
 *    1080x1920 panel where a stray touch-drag must not move the page. On the
 *    open web it makes anything below the fold *unreachable*, because no
 *    scrollbar ever appears.
 * 2. The class goes on <html> as well as <body>, because Home.js writes the
 *    venue's lobby art onto the document element and that has to be suppressed
 *    in web mode — CSS cannot select upwards from body.
 * 3. The venue logo travels as a custom property so App.css can tile it without
 *    any component knowing the layout exists.
 */
export function applyWebMode(session){
    if (typeof document === "undefined" || !document.body) return;

    var isWeb = isWebSession(session);
    var root = document.documentElement;

    [document.body, root].forEach(function(el){
        if (!el) return;
        if (isWeb) el.classList.add("pt-web-mode");
        else el.classList.remove("pt-web-mode");
    });

    if (!isWeb) {
        removeBackdrop();
        return;
    }
    var logo = (session.displayOptions && session.displayOptions.venueLogoUrl) || "";
    renderBackdrop(logo);
}


/* ---------------------------------------------------------------------------
 * WEB-410 -- the way back in, after the session is gone.
 *
 * The session above lives in sessionStorage, which is exactly right for a
 * session: it dies with the tab, so a shared phone does not hand the next
 * player the last one's visit.
 *
 * But that is also why it cannot answer "where did this player come from".
 * A player who closes the tab and comes back tomorrow has no session, and
 * PrivateRoute would send them to the kiosk login -- a screen they cannot use.
 * So the one fact needed to offer them a new visit, the slug, is kept in
 * localStorage where closing the tab does not reach it.
 *
 * Deliberately only the slug and the names on it. No identity, no token,
 * nothing that would let a returning stranger resume somebody else's visit --
 * this opens the front door, it does not walk through it.
 * ------------------------------------------------------------------------ */

var RETURN_KEY = "pt_web_return";

export function rememberWebReturn(data){
    try {
        if(!data || !data.slug) return;
        localStorage.setItem(RETURN_KEY, JSON.stringify({
            slug: data.slug,
            venueName: data.venueName || "",
            promoName: data.promoName || "",
            // Venue.web_return_url -- where a finished player is sent. Kept
            // here too so the offer survives the tab that carried the session.
            returnUrl: data.returnUrl || ""
        }));
    } catch (e) { /* private mode; the screen degrades to no link */ }
}

export function readWebReturn(){
    try {
        var raw = localStorage.getItem(RETURN_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
}

/**
 * WEB-410 -- where an unauthenticated or finished player goes. One answer, in
 * one place.
 *
 * There were three separate redirects to /login: PrivateRoute, Home's failed
 * token refresh, and Logout. Two of them bypass PrivateRoute entirely, which
 * is why fixing only the guard left a web player still landing on the kiosk
 * login -- the route they actually took was Home's refresh failing after they
 * came back to a stale tab.
 *
 * Anything that would have sent somebody to /login asks this instead.
 */
export function exitPath(){
    var session = readRemoteSession();
    if(session && isWebSession(session)) return "/session-ended";
    if(readWebReturn()) return "/session-ended";
    return "/login";
}

export function storeRemoteSession(data){
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) { /* ignore */ }
    var displayOptions = (data && data.displayOptions) || {};
    applyKeyboardMode(displayOptions.keyboard);
    applyWebMode(data);
}

export function readRemoteSession(){
    try {
        var raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
}

export function clearRemoteSession(){
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    clearKeyboardMode();
    if (typeof document !== "undefined" && document.body) {
        document.body.classList.remove("pt-web-mode");
        if (document.documentElement) {
            document.documentElement.classList.remove("pt-web-mode");
        }
        removeBackdrop();
    }
}
